/**
 * Typed access to the runtime environment (`window._env_`, injected by
 * `env-config.js` at deploy time). The only module allowed to touch
 * `window._env_` — every other module goes through this interface.
 */
export interface RuntimeEnv {
  BACKEND_API_BASE_URL?: string;
  apiKey?: string;
  authDomain?: string;
  projectId?: string;
  storageBucket?: string;
  messagingSenderId?: string;
  appId?: string;
  FIREBASE_VALID_KEY?: string;
  [key: string]: unknown;
}

let cachedEnv: RuntimeEnv | null = null;

/** Read the runtime env once and cache it. Never throws. */
export const getEnv = (): RuntimeEnv => {
  if (cachedEnv) {
    return cachedEnv;
  }
  const globalEnv = (window as unknown as { _env_?: RuntimeEnv })._env_;
  cachedEnv = { ...(globalEnv ?? {}) };
  return cachedEnv;
};

/** Test-only seam: drop the cache so the next `getEnv()` re-reads `window`. */
export const resetEnvCache = (): void => {
  cachedEnv = null;
};

/**
 * Read a required string value. Throws a fail-fast error naming the key
 * instead of failing later with `undefined + "/api/..."`.
 */
export const requireEnv = (key: keyof RuntimeEnv): string => {
  const value = getEnv()[key];
  if (typeof value !== "string" || value.length === 0) {
    throw new Error(`${String(key)} is not configured`);
  }
  return value;
};

/** Backend base URL without a trailing slash. */
export const requireBaseUrl = (): string =>
  requireEnv("BACKEND_API_BASE_URL").replace(/\/$/, "");
